import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { useAuth0Data } from '../../../context/Auth0DataContext';

const { FiGift, FiPlus, FiEdit, FiTrash2, FiSave, FiX, FiCheckCircle, FiClock, FiCopy } = FiIcons;

const OffersManagement = () => {
  const { data, addItem, updateItem, deleteItem, dataYear, dataAvailableYears, setDataYear, initializeOffersYear } = useAuth0Data();
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    status: 'active',
    value: 0,
    client: ''
  });

  // Generate year options
  const currentYear = new Date().getFullYear();
  const yearOptions = [...new Set([...dataAvailableYears, currentYear, currentYear + 1])].sort((a, b) => b - a);

  const handleYearChange = (e) => {
    setDataYear(parseInt(e.target.value));
  };

  const handleInitializeYear = async () => {
    const previousYear = dataYear - 1;
    if (window.confirm(`Initialize ${dataYear} by copying offers from ${previousYear}?\n\nThis will copy all offers with their values and status.`)) {
      setIsInitializing(true);
      try {
        await initializeOffersYear(dataYear);
      } catch (error) {
        alert(error.message);
      } finally {
        setIsInitializing(false);
      }
    }
  };

  const handleAdd = () => {
    if (formData.title.trim() && formData.client.trim() && formData.value > 0) {
      addItem('offers', { ...formData, year: dataYear });
      setFormData({ title: '', status: 'active', value: 0, client: '' });
      setIsAdding(false);
    }
  };

  const handleEdit = (offer) => {
    setEditingId(offer.id);
    setFormData({
      title: offer.title,
      status: offer.status,
      value: offer.value,
      client: offer.client
    });
  };

  const handleUpdate = () => {
    updateItem('offers', editingId, formData);
    setEditingId(null);
    setFormData({ title: '', status: 'active', value: 0, client: '' });
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormData({ title: '', status: 'active', value: 0, client: '' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusIcon = (status) => {
    return status === 'active' ? FiCheckCircle : FiClock;
  };

  const getStatusColor = (status) => {
    return status === 'active' ? 'text-green-600 bg-green-100' : 'text-blue-600 bg-blue-100';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-soft border border-gray-100 p-6"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
            <SafeIcon icon={FiGift} className="w-5 h-5 text-orange-600" />
          </div>
          <div className="ml-3">
            <div className="flex items-center gap-3">
              <h3 className="text-xl font-bold text-gray-900">Offers</h3>
              <select
                value={dataYear}
                onChange={handleYearChange}
                className="px-3 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 font-medium bg-white"
              >
                {yearOptions.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <p className="text-sm text-gray-500">{data.offers.length} offers for {dataYear}</p>
          </div>
        </div>
        <button
          onClick={() => setIsAdding(true)}
          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
          Add Offer
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="text-center p-4 bg-green-50 rounded-lg">
          <p className="text-sm text-gray-600">Active Offers</p>
          <p className="text-xl font-bold text-green-600">
            {data.offers.filter(offer => offer.status === 'active').length}
          </p>
        </div>
        <div className="text-center p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-gray-600">In Ideation</p>
          <p className="text-xl font-bold text-blue-600">
            {data.offers.filter(offer => offer.status === 'ideation').length}
          </p>
        </div>
        <div className="text-center p-4 bg-purple-50 rounded-lg">
          <p className="text-sm text-gray-600">Active Value</p>
          <p className="text-xl font-bold text-purple-600">
            {formatCurrency(data.offers.filter(offer => offer.status === 'active').reduce((sum, offer) => sum + offer.value, 0))}
          </p>
        </div>
        <div className="text-center p-4 bg-orange-50 rounded-lg">
          <p className="text-sm text-gray-600">Total Pipeline</p>
          <p className="text-xl font-bold text-orange-600">
            {formatCurrency(data.offers.reduce((sum, offer) => sum + offer.value, 0))}
          </p>
        </div>
      </div>

      {/* Add Form */}
      {isAdding && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="mb-6 p-4 bg-gray-50 rounded-lg"
        >
          <h4 className="font-semibold text-gray-900 mb-4">Add New Offer</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Offer title"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
              <input
                type="text"
                value={formData.client}
                onChange={(e) => setFormData(prev => ({ ...prev, client: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Client name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
              <input
                type="number"
                value={formData.value}
                onChange={(e) => setFormData(prev => ({ ...prev, value: parseInt(e.target.value) || 0 }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Offer value"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="active">Active</option>
                <option value="ideation">Ideation</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button
              onClick={handleAdd}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
              Save
            </button>
            <button
              onClick={handleCancel}
              className="flex items-center px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
            >
              <SafeIcon icon={FiX} className="w-4 h-4 mr-2" />
              Cancel
            </button>
          </div>
        </motion.div>
      )}

      {/* Offers List */}
      <div className="space-y-4">
        {data.offers.map((offer, index) => (
          <motion.div
            key={offer.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="border border-gray-200 rounded-lg p-4"
          >
            {editingId === offer.id ? (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
                    <input
                      type="text"
                      value={formData.client}
                      onChange={(e) => setFormData(prev => ({ ...prev, client: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
                    <input
                      type="number"
                      value={formData.value}
                      onChange={(e) => setFormData(prev => ({ ...prev, value: parseInt(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="active">Active</option>
                      <option value="ideation">Ideation</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleUpdate}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
                    Save
                  </button>
                  <button
                    onClick={handleCancel}
                    className="flex items-center px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                  >
                    <SafeIcon icon={FiX} className="w-4 h-4 mr-2" />
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getStatusColor(offer.status)}`}>
                      <SafeIcon icon={getStatusIcon(offer.status)} className="w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{offer.title}</h4>
                      <p className="text-sm text-gray-600">{offer.client}</p>
                    </div>
                  </div>
                  <div className="ml-11 flex items-center gap-4">
                    <div>
                      <p className="text-lg font-bold text-gray-900">{formatCurrency(offer.value)}</p>
                    </div>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(offer.status)}`}>
                      {offer.status === 'active' ? 'Active' : 'Ideation'}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => handleEdit(offer)}
                    className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiEdit} className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => deleteItem('offers', offer.id)}
                    className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        ))}
      </div>

      {data.offers.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <SafeIcon icon={FiGift} className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p className="mb-4">No offers found for {dataYear}.</p>
          {dataYear > Math.min(...dataAvailableYears) && (
            <button
              onClick={handleInitializeYear}
              disabled={isInitializing}
              className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
            >
              <SafeIcon icon={FiCopy} className="w-4 h-4 mr-2" />
              {isInitializing ? 'Initializing...' : `Initialize ${dataYear} from ${dataYear - 1}`}
            </button>
          )}
          <p className="text-sm mt-4">Or add a new offer to get started.</p>
        </div>
      )}
    </motion.div>
  );
};

export default OffersManagement;